import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { 
  ClipboardCheck, Brain, Calendar, TrendingUp, 
  ArrowRight, X, Sparkles, Shield
} from 'lucide-react';

const steps = [
  {
    icon: ClipboardCheck,
    title: 'Take Your Assessment',
    description: 'Answer questions about your health goals, lifestyle, and current medications. It only takes 5-10 minutes.',
    color: 'bg-blue-100 text-blue-600'
  },
  {
    icon: Brain,
    title: 'Get AI-Powered Recommendations',
    description: 'Our AI analyzes your profile, checks for interactions, and creates a personalized supplement protocol just for you.',
    color: 'bg-purple-100 text-purple-600'
  },
  {
    icon: Shield,
    title: 'Share with Your Doctor',
    description: 'Download your results as a PDF or share via email. Always consult your healthcare provider before starting supplements.',
    color: 'bg-green-100 text-green-600'
  },
  {
    icon: TrendingUp,
    title: 'Track Your Progress',
    description: 'Use our journal and symptom tracker to see what\'s working. Update your answers every 3 months for current recommendations.',
    color: 'bg-amber-100 text-amber-600'
  }
];

export default function OnboardingModal({ isOpen, onClose }) {
  const [currentStep, setCurrentStep] = useState(0);

  useEffect(() => {
    if (isOpen) {
      setCurrentStep(0);
    }
  }, [isOpen]);

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handleSkip = () => {
    localStorage.setItem('vitala_onboarding_seen', 'true');
    onClose();
  };

  if (!isOpen) return null;

  const step = steps[currentStep];
  const StepIcon = step.icon;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && handleSkip()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl max-w-md w-full overflow-hidden"
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-border">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              <span className="font-medium text-[#1A1A1A]">Welcome to Vitala</span>
            </div>
            <button
              onClick={handleSkip}
              className="p-1 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X className="w-5 h-5 text-muted-foreground" />
            </button>
          </div>

          {/* Content */}
          <div className="p-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="text-center"
              >
                <div className={`w-16 h-16 rounded-full ${step.color} flex items-center justify-center mx-auto mb-4`}>
                  <StepIcon className="w-8 h-8" />
                </div>
                <h3 className="text-xl font-semibold text-[#1A1A1A] mb-2">{step.title}</h3>
                <p className="text-muted-foreground">{step.description}</p>
              </motion.div>
            </AnimatePresence>

            {/* Progress Dots */}
            <div className="flex justify-center gap-2 mt-6">
              {steps.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentStep(index)}
                  className={`w-2 h-2 rounded-full transition-colors ${
                    index === currentStep ? 'bg-primary' : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-border flex items-center justify-between">
            <button
              onClick={handleSkip}
              className="text-sm text-muted-foreground hover:text-[#1A1A1A] transition-colors"
            >
              Skip tutorial
            </button>
            <Button onClick={handleNext} className="btn-primary">
              {currentStep < steps.length - 1 ? (
                <>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              ) : (
                "Get Started"
              )}
            </Button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
